/* HEADER BEHAVIOUR - loaded deferred by sections/custom-header-revised.liquid.
   PART 1  overlay state (transparent vs solid bar)
   PART 2  commerce controls (cart opens the drawer, account hover menu)
   ==========================================================================
   PART 1 - OVERLAY STATE

   On the home page the bar is laid over the hero video. Two classes switch it,
   both styled in assets/amc-header-overlay.css:
     .is-stuck   solid white bar, navy logo and links
     .is-panel   a mega menu, search field or mobile drawer is actually open
   With neither the bar is transparent and the video runs up behind it. EITHER
   class destroys that, so both must be false at rest.

   THE SCROLL TRIGGER - seven versions. Each wrong one failed one of two ways:
     TOO LATE   hero copy scrolls THROUGH the transparent bar, then the hero ends
                and white links sit on a pale section.
     TOO EARLY  bar is solid at rest, so the blended look never appears.

     v1  hero BOTTOM reaches the bar              too late
     v2  hero CONTENT BLOCK reaches the bar       too late
     v3  scrolled>8 OR content block collides     too early - the content block is
         vertically centred, so at rest its top is already inside the bar. True at
         scroll zero; the bar was born white.
     v4/v5  scrolled > 8px                        too early - fires on the first
         flick of the wheel, nobody sees the video.
     v6  scrolled > 300px + hero-bottom safety    too late. 300px is a FIXED
         distance and the viewport is not: measured on a 508px-tall viewport, at
         exactly 300px the eyebrow was at -77px, already through the bar.
     v7  whichever comes FIRST of three, all gated on having scrolled. <-- current

   WHY THREE. The dwell is what was asked for - a few wheel turns of video before
   the bar changes - but a dwell in pixels only behaves on a viewport tall enough
   that the copy has not reached the bar yet. So the copy collision runs alongside
   it and wins on short viewports, and the hero-bottom test backstops both.

   WHY NONE CAN BE TRUE AT REST: shouldSolidify returns false unless the page has
   actually been scrolled. That guard is what makes the geometry safe and must stay.

   TUNING: change DWELL (wheel notches) or COPY_LEAD. Do NOT add a test that
   measures the content BLOCK rather than the copy inside it - that is the v3 bug.
   ========================================================================== */
(function () {
  'use strict';

  var NOTCH = 100;
  var DWELL = 3 * NOTCH;
  var COPY_LEAD = 8;
  var SAFETY_LEAD = 20;
  var PANEL_SELECTOR = '.amc-hdr__mega,.amc-hdr__search,.amc-hdr__drawer';

  var root = null, hero = null, copy = null;
  var queuedScroll = false, queuedPanel = false, panelOn = false;

  function barEl()  { return document.querySelector('.amc-hdr'); }
  function heroEl() { return document.querySelector('.amc-opening-hero'); }

  // The FIRST VISIBLE line of hero copy - not the block containing it. The block
  // is centred and padded, so its top is already behind the bar at rest.
  function copyEl() {
    return document.querySelector('.amc-opening-hero__eyebrow') ||
           document.querySelector('.amc-opening-hero h1') ||
           document.querySelector('.amc-opening-hero__content > *');
  }

  function shouldSolidify() {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (y <= 0) return false;
    if (y > DWELL) return true;
    var barBottom = root.getBoundingClientRect().bottom;
    if (!copy || !copy.isConnected) copy = copyEl();
    if (copy && copy.getBoundingClientRect().top <= barBottom + COPY_LEAD) return true;
    if (!hero || !hero.isConnected) hero = heroEl();
    if (hero && hero.getBoundingClientRect().bottom <= barBottom + SAFETY_LEAD) return true;
    return false;
  }

  function paintScroll() {
    queuedScroll = false;
    if (!root || !root.isConnected) root = barEl();
    if (!root) return;
    if (!root.classList.contains('amc-hdr--overlay')) { root.classList.add('is-stuck'); return; }
    var stuck = shouldSolidify();
    if (root.classList.contains('is-stuck') !== stuck) root.classList.toggle('is-stuck', stuck);
  }

  function onScroll() {
    if (queuedScroll) return;
    queuedScroll = true;
    requestAnimationFrame(paintScroll);
  }

  /* Panels are revealed three different ways:
       .amc-hdr__mega     never uses [hidden]; flips visibility/opacity
       .amc-hdr__search   never uses [hidden]; container collapsed, so not laid out
       .amc-hdr__drawer   uses [hidden] plus display:none
     An earlier version tested :not([hidden]) and so matched four megas and the
     search field permanently - .is-panel was on from first paint. isOpen asks the
     only question covering all three: can the visitor actually see it. */
  function isOpen(el) {
    if (!el || el.hasAttribute('hidden')) return false;
    // Mega menus open purely on CSS :hover / :focus-within of their nav item. Read
    // that state directly instead of the fade (opacity/visibility), which lags the
    // pointer by ~250ms and left the bar white with white links after mouse-out.
    if (el.classList && el.classList.contains('amc-hdr__mega')) {
      // Mega menus never solidify the bar: on the home video the bar stays navy with
      // white links and the panel drops below it; once scrolled, .is-stuck already
      // makes it white with navy links. Only search and the mobile drawer use .is-panel.
      return false;
    }
    var css = getComputedStyle(el);
    if (css.display === 'none' || css.visibility === 'hidden') return false;
    if (parseFloat(css.opacity) <= 0.05) return false;
    if (!el.offsetParent && css.position !== 'fixed') return false;
    return true;
  }

  function paintPanel() {
    queuedPanel = false;
    if (!root || !root.isConnected) root = barEl();
    if (!root) return;
    var panels = document.querySelectorAll(PANEL_SELECTOR);
    var open = false;
    for (var i = 0; i < panels.length; i++) {
      if (isOpen(panels[i])) { open = true; break; }
    }
    if (open === panelOn) return;
    panelOn = open;
    root.classList.toggle('is-panel', open);
  }

  // getComputedStyle on six elements per frame while the mouse moves anywhere on
  // the page makes scrolling feel sticky. Only do the work when the pointer is in
  // the header, or when a panel is open and might need closing.
  function onPanel(event) {
    if (event && event.target && event.target.closest) {
      if (!panelOn && !event.target.closest('.amc-hdr')) return;
    }
    // Mega menus fade out over ~250ms, so a single check right after the pointer
    // leaves still sees the panel as visible and .is-panel stuck on. Re-check once
    // the fade has finished (2026-10-13).
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () { requestAnimationFrame(paintPanel); }, 420);
    if (queuedPanel) return;
    queuedPanel = true;
    requestAnimationFrame(paintPanel);
  }
  var settleTimer;

  function startOverlay() {
    root = barEl();
    if (!root) return;
    hero = heroEl();
    copy = copyEl();
    paintScroll();
    paintPanel();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    ['pointerover', 'pointerout', 'focusin', 'focusout', 'click', 'keyup'].forEach(function (type) {
      root.addEventListener(type, onPanel, true);
    });
    document.addEventListener('pointerover', onPanel, true);
    try {
      root.querySelectorAll('.amc-hdr__search,.amc-hdr__drawer').forEach(function (panel) {
        new MutationObserver(function () { onPanel(); }).observe(panel, {
          attributes: true, attributeFilter: ['hidden', 'class', 'style', 'aria-expanded']
        });
      });
    } catch (e) {}
  }

  /* ==========================================================================
     PART 2 - COMMERCE CONTROLS

     THE CART ICON. The theme ships a fully built <cart-drawer> with an open()
     API, item rows, quantity steppers, View cart, Checkout and Continue shopping.
     The theme normally wires the header icon to it in the drawer's own
     setHeaderCartIconAccessibility(), which looks for the stock icon id. This
     store uses a custom header whose cart control is .amc-hdr__cart, so that
     wiring never found it and the icon stayed a plain link to /cart - a full page
     load on every cart glance, with the drawer sitting unused in the DOM.

     openCart below closes that gap. The anchor KEEPS href="/cart": that is the
     no-JS path, the middle-click path and the right-click path, and it is why the
     handler bails out on modified clicks instead of swallowing them.

     THE ACCOUNT MENU. Hovering the account icon showed nothing; you had to click
     through to find out what was there. The menu is built here rather than in
     Liquid because adding it to the header section would mean rewriting a large
     file for a few lines of markup.

     It links to /account rather than /account/login or /account/register. This
     store runs NEW customer accounts - the account URL redirects to
     shopify.com/<id>/account, and that flow handles signing in and creating an
     account in one path, so there is no separate register route to link to. The
     signed-in state is not detectable from the client here, which is the other
     reason the first item is worded to cover both cases rather than claiming the
     visitor is logged out.

     The menu opens on hover AND focus-within, so it is reachable by keyboard, and
     it is suppressed on touch/mobile in CSS, where a hover menu cannot be used and
     the icon should simply navigate.
     ========================================================================== */

  function openCart(event) {
    // Let the browser handle anything that is not a plain left click.
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;  // fall through to /cart

    event.preventDefault();
    drawer.open(event.currentTarget);
  }

  function startCommerce() {
    var bar = barEl();
    if (!bar) return;

    var cart = bar.querySelector('.amc-hdr__cart');
    if (cart && cart.dataset.amcCartReady !== 'true') {
      cart.dataset.amcCartReady = 'true';
      cart.setAttribute('aria-haspopup', 'dialog');
      cart.setAttribute('aria-expanded', 'false');
      cart.addEventListener('click', openCart);
      var hoverTimer, leaveTimer;
      var hoverMedia = window.matchMedia('(min-width: 960px) and (hover: hover) and (pointer: fine)');
      function drawerElement() { return document.querySelector('cart-drawer'); }
      function cancelLeave() { clearTimeout(leaveTimer); }
      function scheduleLeave() {
        clearTimeout(hoverTimer);
        cancelLeave();
        leaveTimer = setTimeout(function () {
          var drawer = drawerElement();
          if (drawer?.openedFromHover) drawer.close();
        }, 240);
      }
      cart.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'mouse' || !hoverMedia.matches) return;
        cancelLeave();
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(function () {
          var drawer = drawerElement();
          if (!drawer || typeof drawer.open !== 'function' || drawer.classList.contains('active')) return;
          if (document.body.classList.contains('overflow-hidden') || document.body.classList.contains('klaviyo-prevent-body-scrolling')) return;
          drawer.open(cart, true);
        }, 180);
      });
      cart.addEventListener('pointerleave', scheduleLeave);
      var drawer = drawerElement();
      if (drawer && !drawer.dataset.amcHoverReady) {
        drawer.dataset.amcHoverReady = 'true';
        drawer.addEventListener('pointerover', function (event) {
          if (event.target.closest('.drawer__inner')) cancelLeave();
          else if (drawer.openedFromHover) scheduleLeave();
        });
        drawer.addEventListener('pointerleave', scheduleLeave);
        drawer.addEventListener('focusin', cancelLeave);
        drawer.addEventListener('pointerdown', function (event) {
          if (drawer.openedFromHover && event.target.closest('.drawer__inner')) {
            cancelLeave();
            drawer.open(cart);
          }
        });
      }
    }

    // The Liquid header owns the single account panel and its interactions.
  }

  function start() {
    startOverlay();
    startCommerce();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // A bfcache restore keeps the old classes but can restore a different scroll
  // position, so re-derive both states on the way back in.
  window.addEventListener('pageshow', function () { paintScroll(); paintPanel(); });
})();
